<script setup lang="ts">
import type { BundledGoodsInfo, GoodsReviewInfo, ReviewSummaryInfo } from '~/types/goods/goods-detail'
import { extractPayloadTransform } from '~/transforms/goods/goods-transform'
import type { ShopApiResponse } from '~/types/common/shop-api-response'
import { getGoodsReviewInfo, getGoodsReviewList } from '~/services/goods/review-service'
import { getImgBaseUrl, isNotEmptyArray } from '~/utils/shop-common'

const { $filter } = useNuxtApp()

const { goodsNo: propsGoodsNo, bundleGoodsNo, prRelGoodsInfo } = defineProps<{
  goodsNo: string
  bundleGoodsNo?: string
  prRelGoodsInfo?: BundledGoodsInfo[]
}>()
const tabData = defineModel<object>({ default: null })

const goodsNo = ref(propsGoodsNo)
const revSummaryDto = ref<ReviewSummaryInfo>()
const topRankEvaluationList = ref()
const contentThumbnailList = ref()
const sortingData = reactive({ id: 1, title: '별점높은순', sortTypeCd: 'revScrDesc' })

const infinityScrollList = ref(null)
const paging = reactive({ pageNo: 1, pageSize: 20 })

// 리뷰 펼치기 토글 버튼
const isReviewToggle = ref(false)

const { data, refresh } = useLocaAsyncData<ShopApiResponse<GoodsReviewInfo>, GoodsReviewInfo>
(`getGoodsReviewInfo:${goodsNo.value}`, () => getGoodsReviewInfo(goodsNo.value, bundleGoodsNo), { transform: extractPayloadTransform }, false)

watch(data, () => {
  init()
})

watch(
  goodsNo, async () => {
    await refresh()
  },
)

function init() {
  revSummaryDto.value = data.value.revSummaryDto
  topRankEvaluationList.value = data.value.topRankEvaluationList
  contentThumbnailList.value = data.value.contentThumbnailList
  if (tabData.value) {
    tabData.value.updateTitle(isEmptyValue(bundleGoodsNo) ? revSummaryDto.value?.revTotCnt : data.value.bundleGoodsRevTotCnt)
  }
  clearList()
}

async function fetchGoodsReviewList() {
  return await getGoodsReviewList(goodsNo.value, { ...paging, sortTypeCd: sortingData.sortTypeCd }).then(res => res.payload)
}

function openTotalReview(review, index) {
  if (index !== 3) { // 리뷰상세
    const sortTypeCd = { sortTypeCd: 'latest' }
    const { open: openReviewPopup } = useReviewDetailModal(review, sortTypeCd, true)
    openReviewPopup()
  }
  else { // 더보기일 경우에는 사진 모아보기
    const router = useRouter()
    router.push(`/goods/TotalReview?goodsNo=${goodsNo.value}`)
  }
}

const setReviewAveragePercent = (value: number) => {
  return value / 5 * 100
}

const getMaxPercentage = (list: { evltValPrcnt: number }[]) => {
  return Math.max(...list.map(item => item.evltValPrcnt))
}

const reviewToggleHandler = () => {
  isReviewToggle.value = !isReviewToggle.value
}

const clearList = () => {
  if (infinityScrollList.value)
    infinityScrollList.value.clearList()
  close()
}

function onSuccessReport() {
  refresh()
  // init()
  // infinityScrollList.value.clearList()
}

function setGoodsNo(selectedGoodsNo: string) {
  goodsNo.value = selectedGoodsNo
}
/* 정렬 Modal */
const { close, open: openReviewSort } = useReviewSortingOptionModal(sortingData, clearList)
</script>

<template>
  <template v-if="isNotEmptyArray(prRelGoodsInfo)">
    <GoodsDetailBundleGoodsList
      :goods-no="goodsNo"
      :pr-rel-goods-info="prRelGoodsInfo"
      @select-goods="setGoodsNo"
    />
    <div class="divider-01" />
  </template>
  <div
    v-if="revSummaryDto?.revTotCnt"
  >
    <div class="py-6 px-4 border-none">
      <div>
        <p class="text-16 text-on-view-02 text-center">
          구매자 만족도
        </p>

        <div class="flex items-center justify-center mt-1">
          <div class="relative w-[120px] mr-[6px]">
            <div class="flex">
              <template
                v-for="(star, index) of 5"
                :key="index"
              >
                <i-graphic-star-gray-l class="w-6 h-6" />
              </template>
            </div>
            <div
              class="absolute top-0 left-0 flex text-[0px]"
              :style="{ clipPath: `polygon(0 0, ${setReviewAveragePercent(revSummaryDto.avgScr)}% 0, ${setReviewAveragePercent(revSummaryDto.avgScr)}% 100%, 0% 100%)` }"
            >
              <template
                v-for="(star, index) of 5"
                :key="index"
              >
                <i-graphic-star-l class="w-6 h-6" />
              </template>
            </div>
          </div>
          <span class="text-20 font-bold">
            <span class="sr-only">구매만족도 5점 중 </span>
            {{ revSummaryDto.avgScr }}점
          </span>
        </div>
      </div>

      <div
        v-if="isNotEmptyArray(topRankEvaluationList)"
        class="mt-4 p-4 bg-warm-gray-30"
      >
        <div
          v-for="(item, index) in topRankEvaluationList"
          :key="index"
          class="mt-2 first:mt-0"
        >
          <div class="flex items-center justify-between">
            <span class="min-w-[48px] mr-2 text-12 px-1.5 text-center border border-devider-02">{{ item.evltItemNm }}</span>
            <span class="mr-2 text-14 font-medium">{{ item.evltVal }}</span>
            <span class="grow mr-1.5 h-[1px] border-t border-dashed border-devider-02" />
            <span class="w-[38px] text-right text-14 font-medium">{{ item.evltValPrcnt }}%</span>
          </div>
          <div
            v-show="isReviewToggle"
            class="py-2"
          >
            <div
              v-for="(evaluation, index2) in item.evaluations"
              :key="index2"
              class="flex items-center justify-between mt-[3px] first:mt-0"
            >
              <span
                class="grow text-12 text-warm-gray-400"
                :class="{ '!text-on-view-01 font-medium': evaluation.evltValPrcnt === getMaxPercentage(item.evaluations) }"
              >{{ evaluation.evltVal }}</span>
              <div class="w-[140px] h-1 mr-1.5 bg-warm-gray-100">
                <div
                  class="h-1 bg-warm-gray-300"
                  :class="{ '!bg-warm-gray-600': evaluation.evltValPrcnt === getMaxPercentage(item.evaluations) }"
                  :style="{ width: evaluation.evltValPrcnt + '%' }"
                />
              </div>
              <span
                class="w-9 text-12 text-right text-warm-gray-400"
                :class="{ 'text-warm-gray-800 font-medium': evaluation.evltValPrcnt === getMaxPercentage(item.evaluations) }"
              >{{ evaluation.evltValPrcnt }}%</span>
            </div>
          </div>
        </div>
        <button
          type="button"
          class="flex items-center justify-center w-full mt-4 gap-1"
          @click="reviewToggleHandler"
        >
          <template v-if="isReviewToggle">
            <span class="text-14 font-medium">접기</span>
            <i-normal-arrow-up-onview-01 class="w-6 h-6" />
          </template>
          <template v-else>
            <span class="text-14 font-medium">펼쳐보기</span>
            <i-normal-arrow-down-onview-01 class="w-6 h-6" />
          </template>
        </button>
      </div>

      <div
        v-if="isNotEmptyArray(contentThumbnailList)"
        class="mt-4"
      >
        <ul class="grid grid-cols-4 gap-1">
          <li
            v-for="(item, index) in contentThumbnailList.slice(0, 4)"
            :key="item.id"
            class="inline-flex"
          >
            <button
              type="button"
              class="relative"
              data-gtm-click
              :data-gtm-body="useGAHandler().generateGTMData({
                label: '리뷰상세',
              })"
              @click="openTotalReview(item, index)"
            >
              <div class="relative overflow-hidden aspect-square w-full">
                <ShopCommonImage
                  :src="getImgBaseUrl(item.contents[0].contFilePathNm, '160', '160')"
                  alt="리뷰 이미지"
                />
              </div>
              <span
                v-if="item.contents.length > 1"
                class="absolute bottom-0 right-0 inline-flex"
              >
                <span class="shop-c-label pr-1.5 text-on-view-06 bg-warm-gray-800">
                  <i-normal-plus-s-thin-onview-06 class="w-4 h-4" />
                  <span class="-ml-px">{{ item.contents.length }}</span>
                </span>
              </span>
              <span
                v-if="index === 3"
                class="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-on-view-01/60 text-14 font-medium text-on-view-06"
              >
                <i-normal-plus-s-thin-onview-06 class="w-4 h-4" />
                더보기
              </span>
            </button>
          </li>
        </ul>
      </div>
    </div>
    <div class="divider-thick bg-warm-gray-30" />
    <div>
      <div class="flex items-center justify-between pt-6 px-4 pb-2">
        <strong class="text-18 font-bold">리뷰 <span class="text-on-view-03">({{ $filter.numToLocaleStr(revSummaryDto.revTotCnt) }})</span></strong>
        <button
          type="button"
          class="inline-flex items-center gap-1"
          aria-label="리뷰 정렬 팝업 열기"
          @click="() => openReviewSort()"
        >
          <span class="text-14 font-medium">{{ sortingData.title }}</span>
          <i-normal-arrow-down-onview-01 class="w-6 h-6" />
        </button>
      </div>
      <ShopInfinityScrollList
        ref="infinityScrollList"
        v-model:paging="paging"
        :async-key="goodsNo"
        :total-size="revSummaryDto.revTotCnt"
        :request="fetchGoodsReviewList"
        :is-cache="false"
      >
        <template #default="{ item, index }">
          <div :class="index + 1 === revSummaryDto.revTotCnt ? 'px-4 pt-4 p-6' : 'p-4 border-b border-warm-gray-100'">
            <GoodsDetailReviewRow
              :review-data="item"
              :sorting-data="sortingData"
              @success-report="onSuccessReport"
            />
          </div>
        </template>
      </ShopInfinityScrollList>
    </div>
  </div>
  <!-- [S] Empty -->
  <ShopGuidance
    v-else
    title="등록된 리뷰가 없어요"
  >
    <template #icon>
      <i-nodata-review class="w-10 h-10" />
    </template>
  </ShopGuidance>
  <!-- [E] Empty -->
</template>

<style scoped>

</style>
